import type { DecodePuzzleDefinition, DecodeSegmentView } from '@aztec-realms/shared-types';

import type { DecodePort, DecodeVisualState } from './decode-visual-state';

const fullCircle = Math.PI * 2;
const portOrder: readonly DecodePort[] = ['inner', 'clockwise', 'outer', 'counter_clockwise'];

function sectorCount(puzzle: DecodePuzzleDefinition, ringIndex: number): number {
  return Math.max(1, puzzle.sectorsPerRing[ringIndex] ?? 1);
}

export function decodeSegmentCenterAngle(
  puzzle: DecodePuzzleDefinition,
  segment: DecodeSegmentView,
): number {
  const slots = sectorCount(puzzle, segment.ringIndex);
  return -Math.PI / 2 + ((segment.angularSlot + 0.5) / slots) * fullCircle;
}

export function orientedDecodePort(basePort: DecodePort, rotation: number): DecodePort {
  const baseIndex = portOrder.indexOf(basePort);
  const offset = ((Math.round(rotation) % portOrder.length) + portOrder.length) % portOrder.length;
  return portOrder[(baseIndex + offset) % portOrder.length] ?? basePort;
}

function angularBoundaryAngle(
  puzzle: DecodePuzzleDefinition,
  segment: DecodeSegmentView,
  port: 'clockwise' | 'counter_clockwise',
): number {
  const slots = sectorCount(puzzle, segment.ringIndex);
  const boundarySlot = segment.angularSlot + (port === 'clockwise' ? 1 : 0);
  return -Math.PI / 2 + (boundarySlot / slots) * fullCircle;
}

function connectedSegmentIds(
  visualState: DecodeVisualState,
  segmentId: string,
  port: DecodePort,
): string[] {
  return visualState.connections.flatMap((connection) => {
    if (connection.leftSegmentId === segmentId && connection.leftPort === port)
      return [connection.rightSegmentId];
    if (connection.rightSegmentId === segmentId && connection.rightPort === port)
      return [connection.leftSegmentId];
    return [];
  });
}

/**
 * Returns the exact seam anchors used by a visible channel port.
 *
 * Adjacent rings may contain different sector counts. Their stone centers therefore do not
 * share an angle even though the authoritative topology connects them. Both sides are anchored
 * to the inner stone's center angle, making every accepted graph edge a continuous carved seam.
 * Multiple inward edges deliberately produce a visible branch instead of a hidden many-to-one
 * connection.
 */
export function decodePortAnchorAngles(
  puzzle: DecodePuzzleDefinition,
  visualState: DecodeVisualState,
  segment: DecodeSegmentView,
  port: DecodePort,
): readonly number[] {
  if (port === 'clockwise' || port === 'counter_clockwise') {
    return [angularBoundaryAngle(puzzle, segment, port)];
  }

  const linkedSegments = connectedSegmentIds(visualState, segment.id, port)
    .map((segmentId) => puzzle.segments.find((candidate) => candidate.id === segmentId))
    .filter((candidate): candidate is DecodeSegmentView => candidate !== undefined);

  if (linkedSegments.length === 0) return [decodeSegmentCenterAngle(puzzle, segment)];

  const anchors = linkedSegments.map((linkedSegment) =>
    decodeSegmentCenterAngle(
      puzzle,
      linkedSegment.ringIndex < segment.ringIndex ? linkedSegment : segment,
    ),
  );
  return [...new Set(anchors)];
}
